import PropTypes from 'prop-types';
import {
  ButtonGroup,
  Flex,
  Heading,
  LoadingButton,
  ModalDialog,
  Paragraph,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation';
import useRequestApplicationManagerIdVerification from '../../../../utils/apis/useRequestApplicationManagerIdVerification';
import { ERROR_MSGS, getApplicationManagerName } from './RequestIdVerificationModal.util';

export default function RequestIdVerificationModal({
  isOpen,
  onClose,
  refetchApplicationManagerQuery,
  employees = [],
}) {
  const applicationManagerName = getApplicationManagerName(employees);
  const { enqueueToast } = useToast();
  const { isPending, mutate } = useRequestApplicationManagerIdVerification({
    onSuccess() {
      enqueueToast(
        <Toast appearance="success" body={`We've sent an ID verification request to ${applicationManagerName}`} />,
      );
      onClose();
    },
    onError(error) {
      switch (error?.response?.data?.error?.message ?? '') {
        case ERROR_MSGS.APPLICATION_MANAGER_ALREADY_ID_VERIFIED:
        case ERROR_MSGS.IS_APPLICATION_MANAGER: {
          refetchApplicationManagerQuery();
          onClose();
          // falls through
        }
        default: {
          enqueueToast(
            <Toast
              appearance="error"
              body="An error occurred while sending your request for identity verification. Please try again."
            />,
          );
        }
      }
    },
  });

  return (
    <ModalDialog
      renderBody={
        <Flex display="flex" flexDirection="column" alignItems="flex-start">
          <Heading level={4} as="h2" marginBottom="sm">
            You can’t view application details now
          </Heading>
          <Paragraph fontType="bodySmallHeading" marginBottom="xs">
            Why?
          </Paragraph>
          <Paragraph marginBottom="md">
            This content cannot be viewed until your {applicationManagerName} verifies their identity on Zillow Rental
            Manager. This step is required to comply with the Fair Credit Reporting Act.
          </Paragraph>
          <Paragraph>Please have them log into their account and verify their identity.</Paragraph>
        </Flex>
      }
      renderFooter={
        <ButtonGroup aria-label="Request ID verification actions">
          <TextButton onClick={onClose} id="cancel-id-verification-request">
            Cancel
          </TextButton>
          <LoadingButton loading={isPending} onClick={mutate} id="send-id-verification-request" buttonType="primary">
            Send request
          </LoadingButton>
        </ButtonGroup>
      }
      isOpen={isOpen}
      onClose={onClose}
    />
  );
}

RequestIdVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchApplicationManagerQuery: PropTypes.func.isRequired,
  employees: PropTypes.array,
};
